import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Rating,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Tabs,
  Tab,
} from '@mui/material';
import {
  LocalShipping as TruckIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Star as StarIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  DirectionsCar as CarIcon,
  TwoWheeler as MotorcycleIcon,
  DirectionsBus as BusIcon,
  ArrowForward as ArrowForwardIcon,
  Map as MapIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';

// Import the EnhancedLogisticsTracking component
import EnhancedLogisticsTracking from './EnhancedLogisticsTracking';

interface TransportProvider {
  id: number;
  name: string;
  avatar: string;
  vehicleType: string;
  capacity: string;
  rating: number;
  completedTrips: number;
  location: string;
  price: number;
  availableDate: string;
}

interface TransportRequest {
  id: number;
  cropType: string;
  quantity: string;
  pickupLocation: string;
  destination: string;
  date: string;
  status: 'pending' | 'matched' | 'in-transit' | 'completed';
  budget: number;
  notes: string;
}

// Mock data for transport providers
const MOCK_PROVIDERS: TransportProvider[] = [
  {
    id: 1,
    name: 'John Transport Services',
    avatar: 'https://i.pravatar.cc/150?img=1',
    vehicleType: 'Truck (3 ton)',
    capacity: '3 tons',
    rating: 4.8,
    completedTrips: 156,
    location: 'Nakuru',
    price: 8500,
    availableDate: '2023-03-25',
  },
  {
    id: 2,
    name: 'Quick Movers Ltd',
    avatar: 'https://i.pravatar.cc/150?img=2',
    vehicleType: 'Pickup Truck',
    capacity: '1.5 tons',
    rating: 4.5,
    completedTrips: 89,
    location: 'Nairobi',
    price: 5500,
    availableDate: '2023-03-24',
  },
  {
    id: 3,
    name: 'Farmers Transport Co-op',
    avatar: 'https://i.pravatar.cc/150?img=3',
    vehicleType: 'Truck (7 ton)',
    capacity: '7 tons',
    rating: 4.9,
    completedTrips: 210,
    location: 'Eldoret',
    price: 12000,
    availableDate: '2023-03-26',
  },
  {
    id: 4,
    name: 'Mary Logistics',
    avatar: 'https://i.pravatar.cc/150?img=4',
    vehicleType: 'Van',
    capacity: '800 kg',
    rating: 4.3,
    completedTrips: 67,
    location: 'Kisumu',
    price: 4000,
    availableDate: '2023-03-23',
  },
  {
    id: 5,
    name: 'Reliable Transporters',
    avatar: 'https://i.pravatar.cc/150?img=5',
    vehicleType: 'Truck (5 ton)',
    capacity: '5 tons',
    rating: 4.7,
    completedTrips: 134,
    location: 'Mombasa',
    price: 9500,
    availableDate: '2023-03-27',
  },
];

// Mock data for transport requests
const MOCK_REQUESTS: TransportRequest[] = [
  {
    id: 1,
    cropType: 'Maize',
    quantity: '2 tons',
    pickupLocation: 'Nakuru',
    destination: 'Nairobi',
    date: '2023-03-25',
    status: 'pending',
    budget: 7000,
    notes: 'Need temperature-controlled transport',
  },
  {
    id: 2,
    cropType: 'Potatoes',
    quantity: '1.5 tons',
    pickupLocation: 'Nyandarua',
    destination: 'Nakuru',
    date: '2023-03-26',
    status: 'matched',
    budget: 5000,
    notes: 'Fragile produce, handle with care',
  },
  {
    id: 3,
    cropType: 'Tomatoes',
    quantity: '800 kg',
    pickupLocation: 'Naivasha',
    destination: 'Nairobi',
    date: '2023-03-24',
    status: 'in-transit',
    budget: 4000,
    notes: 'Need quick delivery to maintain freshness',
  },
];

const vehicleTypes = [
  'Any',
  'Motorcycle',
  'Car',
  'Van',
  'Pickup Truck',
  'Truck (1-3 ton)',
  'Truck (3-7 ton)',
  'Truck (7+ ton)',
];

const locations = [
  'Any',
  'Nairobi',
  'Mombasa',
  'Kisumu',
  'Nakuru',
  'Eldoret',
  'Thika',
  'Nyeri',
  'Machakos',
  'Kitale',
  'Naivasha',
  'Nanyuki',
  'Malindi',
  'Kericho',
  'Kakamega',
];

/**
 * LogisticsCoordination component for connecting farmers with transport providers
 * Allows users to find transport services, create transport requests, and manage logistics
 * @returns React component for logistics coordination interface
 */
const LogisticsCoordination: React.FC = () => {
  // State for active tab (0: Find Transport, 1: My Requests, 2: Track Shipments)
  const [activeTab, setActiveTab] = useState(0);
  const [providers, setProviders] = useState<TransportProvider[]>(MOCK_PROVIDERS);
  const [requests, setRequests] = useState<TransportRequest[]>(MOCK_REQUESTS);
  const [filters, setFilters] = useState({
    vehicleType: 'Any',
    location: 'Any',
    minRating: 0,
  });
  const [openNewRequestDialog, setOpenNewRequestDialog] = useState(false);
  const [newRequest, setNewRequest] = useState({
    cropType: '',
    quantity: '',
    pickupLocation: '',
    destination: '',
    date: '',
    budget: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<TransportProvider | null>(null);

  /**
   * Handles tab change between 'Find Transport' and 'My Requests' tabs
   * @param _event - The React synthetic event
   * @param newValue - The index of the newly selected tab
   */
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  /**
   * Handles changes to filter options for transport providers
   * @param event - The change event containing the filter name and new value
   */
  const handleFilterChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    const { name, value } = event.target;
    setFilters({
      ...filters,
      [name as string]: value,
    });
  };

  /**
   * Filters transport providers based on selected filter criteria
   * Filters by vehicle type, location, and minimum rating
   */
  const filteredProviders = providers.filter(provider => {
    if (filters.vehicleType !== 'Any' && !provider.vehicleType.includes(filters.vehicleType)) {
      return false;
    }
    if (filters.location !== 'Any' && provider.location !== filters.location) {
      return false;
    }
    if (provider.rating < filters.minRating) {
      return false;
    }
    return true;
  });

  /**
   * Opens the dialog for creating a new transport request
   */
  const handleNewRequestOpen = () => {
    setOpenNewRequestDialog(true);
  };

  /**
   * Closes the new transport request dialog and resets the form
   */
  const handleNewRequestClose = () => {
    setOpenNewRequestDialog(false);
    setNewRequest({
      cropType: '',
      quantity: '',
      pickupLocation: '',
      destination: '',
      date: '',
      budget: '',
      notes: '',
    });
  };

  /**
   * Handles changes to the new transport request form fields
   * @param e - The change event from input or textarea elements
   */
  const handleNewRequestChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewRequest(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Submits the new transport request form
   * Validates required fields and creates a new request object
   * Simulates an API call with setTimeout
   */
  const handleNewRequestSubmit = () => {
    if (
      !newRequest.cropType ||
      !newRequest.quantity ||
      !newRequest.pickupLocation ||
      !newRequest.destination ||
      !newRequest.date ||
      !newRequest.budget
    )
      return;

    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      const newRequestObj: TransportRequest = {
        id: Math.max(...requests.map(r => r.id)) + 1,
        cropType: newRequest.cropType,
        quantity: newRequest.quantity,
        pickupLocation: newRequest.pickupLocation,
        destination: newRequest.destination,
        date: newRequest.date,
        status: 'pending',
        budget: parseFloat(newRequest.budget),
        notes: newRequest.notes,
      };

      setRequests([newRequestObj, ...requests]);
      setIsSubmitting(false);
      handleNewRequestClose();
    }, 1000);
  };

  /**
   * Opens the contact dialog for a specific transport provider
   * @param provider - The transport provider to contact
   */
  const handleContactProvider = (provider: TransportProvider) => {
    setSelectedProvider(provider);
    setContactDialogOpen(true);
  };

  /**
   * Closes the contact dialog and resets the selected provider
   */
  const handleContactDialogClose = () => {
    setContactDialogOpen(false);
    setSelectedProvider(null);
  };

  /**
   * Returns a styled chip component based on the transport request status
   * @param status - The status of the transport request (pending, matched, in-transit, completed)
   * @returns A Chip component with appropriate color and label
   */
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'pending':
        return <Chip size="small" label="Pending" color="warning" />;
      case 'matched':
        return <Chip size="small" label="Matched" color="info" />;
      case 'in-transit':
        return <Chip size="small" label="In Transit" color="primary" />;
      case 'completed':
        return <Chip size="small" label="Completed" color="success" />;
      default:
        return <Chip size="small" label={status} />;
    }
  };

  /**
   * Returns an appropriate icon based on the vehicle type
   * @param vehicleType - The type of vehicle (Truck, Pickup, Van, Motorcycle)
   * @returns A Material-UI icon component representing the vehicle type
   */
  const getVehicleIcon = (vehicleType: string) => {
    if (vehicleType.includes('Truck')) {
      return <TruckIcon />;
    } else if (vehicleType.includes('Pickup')) {
      return <CarIcon />;
    } else if (vehicleType.includes('Van')) {
      return <BusIcon />;
    } else if (vehicleType.includes('Motorcycle')) {
      return <MotorcycleIcon />;
    } else {
      return <TruckIcon />;
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <EnhancedLogisticsTracking />
            </Box>
    </Container>
  );
};

export default LogisticsCoordination;